import PropTypes from 'prop-types';
import { Input, Button, Dropdown, Progress } from 'antd';
import FeatherIcon from 'feather-icons-react';
import {
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
  SettingOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import styles from './styles/AdvancedTable.module.css';

const TableToolbar = ({
  searchValue = '',
  onSearchChange,
  showFilters = false,
  onToggleFilters,
  onExport,
  onRefresh,
  onDensityChange,
  density = 'comfortable',
  enableSearch = true,
  enableFilters = true,
  enableExport = true,
  enableColumnManager = true,
  enableDensity = false,
  activeFilterCount = 0,
  customActions = null,
  onColumnManagerClick,
  exportLoading = false,
  exportProgress = 0,
  enableHorizontalScroll = false,
  enableRefresh = false,
  enableHorizontalScrollFeature = false,
  onToggleHorizontalScroll,
}) => {
  const densityItems = [
    {
      key: 'compact',
      label: 'Compact',
      icon: <FeatherIcon icon="minus" size={14} />
    },
    {
      key: 'comfortable',
      label: 'Comfortable',
      icon: <FeatherIcon icon="menu" size={14} />
    },
    {
      key: 'spacious',
      label: 'Spacious',
      icon: <FeatherIcon icon="align-justify" size={14} />
    },
  ];

  const exportItems = [
    {
      key: 'csv',
      label: 'Export as CSV',
      icon: <FeatherIcon icon="file-text" size={14} />,
    },
    // {
    //   key: 'excel',
    //   label: 'Export as Excel',
    //   icon: <FeatherIcon icon="file" size={14} />,
    // },
    // {
    //   key: 'pdf',
    //   label: 'Export as PDF',
    //   icon: <FeatherIcon icon="file-text" size={14} />,
    // },
    // {
    //   type: 'divider',
    // },
    // {
    //   key: 'csv-selected',
    //   label: 'Export Selected (CSV)',
    //   icon: <FeatherIcon icon="check-square" size={14} />,
    //   disabled: false, // Will be handled by parent component
    // },
    // {
    //   key: 'excel-selected',
    //   label: 'Export Selected (Excel)',
    //   icon: <FeatherIcon icon="check-square" size={14} />,
    //   disabled: false,
    // },
  ];

  return (
    <div className={styles.tableToolbar}>
      <div className={styles.toolbarLeft}>
        {enableSearch && (
          <div className={styles.searchContainer}>
            <Input
              className={styles.searchInput}
              placeholder="Search..."
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={e => onSearchChange && onSearchChange(e.target.value)}
              allowClear
              size="middle"
            />
          </div>
        )}

        {enableFilters && (
          <Button
            icon={<FilterOutlined />}
            onClick={onToggleFilters}
            type={showFilters ? 'primary' : 'default'}
            size="middle"
          >
            Filters
            {activeFilterCount > 0 && (
              <span className={styles.filterBadge}>
                {activeFilterCount}
              </span>
            )}
          </Button>
        )}
      </div>

      <div className={styles.toolbarRight}>
        {customActions}

        {enableExport && (
          <div className={styles.exportContainer}>
            <Dropdown
              menu={{
                items: exportItems,
                onClick: ({ key }) => onExport && onExport(key),
              }}
              trigger={['click']}
              disabled={exportLoading}
            >
              <Button
                icon={<DownloadOutlined />}
                loading={exportLoading}
                size="middle"
              >
                Export
                {exportProgress > 0 && exportProgress < 100 && (
                  <span style={{ marginLeft: 8 }}>({exportProgress}%)</span>
                )}
              </Button>
            </Dropdown>

            {exportLoading && exportProgress > 0 && (
              <div className={styles.exportProgress}>
                <Progress
                  percent={exportProgress}
                  size="small"
                  showInfo={false}
                  strokeColor="#1890ff"
                />
              </div>
            )}
          </div>
        )}

        {enableDensity && (
          <Dropdown
            menu={{
              items: densityItems,
              selectedKeys: [density],
              onClick: ({ key }) => onDensityChange && onDensityChange(key),
            }}
            trigger={['click']}
          >
            <Button
              icon={<SettingOutlined />}
              size="middle"
              title="Table Density"
            >
              Density
            </Button>
          </Dropdown>
        )}

        {enableColumnManager && (
          <Button
            icon={<SettingOutlined />}
            onClick={onColumnManagerClick}
            size="middle"
            title="Manage Columns"
          >
            Columns
          </Button>
        )}

        {enableHorizontalScrollFeature && (
          <Button
            icon={<FeatherIcon icon={enableHorizontalScroll ? 'minimize-2' : 'maximize-2'} size={14} />}
            onClick={onToggleHorizontalScroll}
            size="middle"
            title={enableHorizontalScroll ? 'Disable Horizontal Scroll' : 'Enable Horizontal Scroll'}
            type={enableHorizontalScroll ? 'primary' : 'default'}
          >
            H-Scroll
          </Button>
        )}

        {enableRefresh && <Button
          icon={<ReloadOutlined />}
          onClick={onRefresh}
          size="middle"
          title="Refresh Data"
        >
          Refresh
        </Button>}
      </div>
    </div>
  );
};

TableToolbar.propTypes = {
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  showFilters: PropTypes.bool,
  onToggleFilters: PropTypes.func,
  onExport: PropTypes.func,
  onRefresh: PropTypes.func,
  onDensityChange: PropTypes.func,
  density: PropTypes.oneOf(['compact', 'comfortable', 'spacious']),
  enableSearch: PropTypes.bool,
  enableFilters: PropTypes.bool,
  enableExport: PropTypes.bool,
  enableColumnManager: PropTypes.bool,
  enableRefresh: PropTypes.bool,
  enableHorizontalScrollFeature: PropTypes.bool,
  enableDensity: PropTypes.bool,
  activeFilterCount: PropTypes.number,
  customActions: PropTypes.node,
  onColumnManagerClick: PropTypes.func,
  exportLoading: PropTypes.bool,
  exportProgress: PropTypes.number,
  enableHorizontalScroll: PropTypes.bool,
  onToggleHorizontalScroll: PropTypes.func,
};

export default TableToolbar;
